import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    Box,
    Container,
    Typography,
    Stack,
    Chip,
    Card,
    CardContent,
    Button,
    TextField,
    MenuItem,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
} from '@mui/material'
import {
    ArrowBack,
    Search,
    Verified,
    Payments,
    Public,
    BusinessCenter,
    Place,
} from '@mui/icons-material'
import {
    diasporaCountries,
    findCountry,
    getBusinessPaymentProfile,
    operatingCountries,
    searchBusinesses,
} from '../utils/businessDirectory'
import api from '../services/api'

const firstNonEmpty = (values) => {
    for (const value of values) {
        const text = String(value || '').trim()
        if (text) return text
    }
    return ''
}

const extractPaymentCode = (rawValue) => {
    const raw = String(rawValue || '').trim()
    if (!raw) return ''
    const upper = raw.toUpperCase()
    if (upper.startsWith('INV-') || upper.startsWith('MP-') || upper.startsWith('WKG-PAY-')) return upper

    const embedded = raw.match(/\b(?:INV|MP|WKG-PAY)-[A-Z0-9-]+\b/i)
    if (embedded) return embedded[0].toUpperCase()

    try {
        const url = new URL(raw)
        const queryCode = firstNonEmpty([
            url.searchParams.get('code'),
            url.searchParams.get('payment_code'),
            url.searchParams.get('paymentCode'),
            url.searchParams.get('payment_reference'),
            url.searchParams.get('paymentReference'),
            url.searchParams.get('invoice'),
            url.searchParams.get('invoice_no'),
            url.searchParams.get('invoice_number'),
            url.searchParams.get('ref'),
            url.searchParams.get('reference'),
        ])
        if (queryCode) return queryCode.toUpperCase()

        const pathParts = url.pathname
            .split('/')
            .filter(Boolean)
            .map((part) => decodeURIComponent(part || '').trim())

        const invoiceIndex = pathParts.findIndex((part) => part.toLowerCase() === 'invoice')
        if (invoiceIndex >= 0 && pathParts[invoiceIndex + 1]) {
            return pathParts[invoiceIndex + 1]
        }

        const pathCode = url.pathname
            .split('/')
            .map((part) => decodeURIComponent(part || '').trim())
            .find((part) => {
                const code = part.toUpperCase()
                return code.startsWith('INV-') || code.startsWith('MP-') || code.startsWith('WKG-PAY-')
            })
        if (pathCode) return pathCode.toUpperCase()
    } catch {
        try {
            const parsed = JSON.parse(raw)
            return firstNonEmpty([
                parsed.code,
                parsed.payment_code,
                parsed.payment_reference,
                parsed.invoice_number,
                parsed.ref,
            ]).toUpperCase()
        } catch {
            return ''
        }
    }

    return ''
}

const buildEzWakaCodeRecord = (rawValue, code) => {
    let url = null
    try {
        url = new URL(rawValue)
    } catch {
        url = null
    }

    const amount = Number(firstNonEmpty([
        url?.searchParams.get('amount'),
        url?.searchParams.get('fare'),
        url?.searchParams.get('total'),
        url?.searchParams.get('price'),
    ]))

    return {
        type: 'payment',
        code,
        merchantName: firstNonEmpty([url?.searchParams.get('merchant'), 'EzWaka']),
        merchantLocation: firstNonEmpty([url?.searchParams.get('route'), url?.searchParams.get('stop'), 'Transport']),
        description: firstNonEmpty([url?.searchParams.get('title'), url?.searchParams.get('description'), 'EzWaka bus fare']),
        amount: Number.isFinite(amount) && amount > 0 ? amount : 0,
        localCurrency: firstNonEmpty([url?.searchParams.get('currency'), 'SLE']).toUpperCase(),
    }
}

const looksLikePosReference = (value) => {
    const code = String(value || '').trim().toUpperCase()
    if (!code.startsWith('MP-') || code.includes('-INV-')) return false
    return code.includes('-POS-') || /^MP-\d{6,}$/.test(code)
}

const eventIdFromCode = (value) => {
    const code = String(value || '').trim().toUpperCase()
    const match = code.match(/\b(?:MP-[A-Z0-9]{2,4}-)?EVT-(\d+)\b/)
    return match ? match[1] : ''
}

const PaymentServices = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [accountType, setAccountType] = useState('USER')
    const [residency, setResidency] = useState('DIASPORA')
    const [residentCountry, setResidentCountry] = useState('US')
    const [country, setCountry] = useState('')
    const [query, setQuery] = useState('')
    const [code, setCode] = useState('')
    const [codeError, setCodeError] = useState('')
    const [codeLoading, setCodeLoading] = useState(false)

    useEffect(() => {
        const entryState =
            location.state && typeof location.state === 'object'
                ? location.state
                : null

        if (entryState) {
            if (entryState.accountType) setAccountType(entryState.accountType)
            if (entryState.residency) setResidency(entryState.residency)
            if (entryState.residentCountry) setResidentCountry(entryState.residentCountry)
            if (entryState.targetCountry !== undefined) setCountry(entryState.targetCountry)
            return
        }

        const saved = window.sessionStorage.getItem('mojopayr-web-entry')
        if (!saved) return

        try {
            const parsed = JSON.parse(saved)
            if (parsed.accountType) setAccountType(parsed.accountType)
            if (parsed.residency) setResidency(parsed.residency)
            if (parsed.residentCountry) setResidentCountry(parsed.residentCountry)
            if (parsed.targetCountry) setCountry(parsed.targetCountry)
        } catch (error) {
            console.error('Failed to read saved MojoPayr web entry', error)
        }
    }, [location.state])

    useEffect(() => {
        if (residency === 'LOCAL') {
            const localCountry = operatingCountries.find(
                (item) => item.code === residentCountry
            )
            setCountry(localCountry?.code ?? 'SL')
            return
        }

        if (!country) return

        const exists = operatingCountries.some((item) => item.code === country)
        if (!exists) setCountry('')
    }, [residency, residentCountry, country])

    useEffect(() => {
        window.sessionStorage.setItem(
            'mojopayr-web-entry',
            JSON.stringify({
                accountType,
                residency,
                residentCountry,
                targetCountry: country,
            })
        )
    }, [accountType, residency, residentCountry, country])

    const residentCountryOptions =
        residency === 'DIASPORA' ? diasporaCountries : operatingCountries

    const providers = useMemo(
        () => searchBusinesses({ countryCode: country, query }),
        [country, query]
    )

    const targetCountryDetails = findCountry(country)
    const searchDisabled = residency === 'DIASPORA' && !country

    const goAmount = (provider) => {
        const paymentProfile = getBusinessPaymentProfile(provider)

        if (paymentProfile.flow === 'bill') {
            navigate('/pay-bills', {
                state: {
                    entryMode: 'directory-bill',
                    countryCode: provider.country,
                    categoryId: paymentProfile.categoryId,
                    providerName: provider.business_name || provider.name,
                    provider,
                    paymentProfile,
                },
            })
            return
        }

        navigate('/payment/amount', {
            state: {
                provider,
                accountType,
                residency,
                residentCountry,
                targetCountry: country,
                paymentProfile,
            },
        })
    }

    const handleProcessCode = async () => {
        const normalizedCode = extractPaymentCode(code)
        const rawCode = String(code || '').trim()
        const looksLikeInvoiceLink = /\/(?:pay\/)?invoice\//i.test(rawCode)

        if (!normalizedCode) {
            setCodeError('Enter a valid payment code or invoice code.')
            return
        }

        if (normalizedCode.startsWith('WKG-PAY-')) {
            const codeRecord = buildEzWakaCodeRecord(code, normalizedCode)
            if (!codeRecord.amount) {
                setCodeError('EzWaka link found, but the amount is missing. Ask EzWaka to generate a payment link with amount and currency.')
                return
            }
            setCodeError('')
            navigate('/payment/confirm', {
                state: {
                    codeFlow: true,
                    codeRecord,
                    residency,
                    residentCountry,
                },
            })
            return
        }

        const eventId = eventIdFromCode(normalizedCode)
        if (eventId) {
            setCodeError('')
            navigate(`/tickets?event=${encodeURIComponent(eventId)}&code=${encodeURIComponent(normalizedCode)}`)
            return
        }

        if (looksLikePosReference(normalizedCode)) {
            setCodeLoading(true)
            try {
                const response = await api.get(`/payments/lookup/${encodeURIComponent(normalizedCode)}`)
                const payment = response.data?.payment
                if (!payment?.reference) {
                    throw new Error('POS payment request was not found.')
                }
                setCodeError('')
                navigate('/payment/confirm', {
                    state: {
                        codeFlow: true,
                        codeRecord: {
                            type: 'pos',
                            code: payment.reference,
                            paymentReference: payment.reference,
                            merchantName: payment.merchantName || 'MojoPayr Merchant',
                            merchantLocation: payment.merchantCountry || '',
                            destinationCountry: payment.merchantCountryCode || payment.merchantCountry || '',
                            description: payment.metadata?.description || 'POS payment',
                            amount: Number(payment.amount || 0),
                            fee: Number(payment.fee || 0),
                            total: Number(payment.total || payment.amount || 0),
                            localCurrency: String(payment.currency || payment.settlementCurrency || 'USD').toUpperCase(),
                            rawSale: payment,
                        },
                        residency,
                        residentCountry,
                    },
                })
            } catch (err) {
                setCodeError(
                    err?.response?.data?.message ||
                    err?.message ||
                    'POS payment request could not be loaded. Ask the merchant to create it again.'
                )
            } finally {
                setCodeLoading(false)
            }
            return
        }

        if (normalizedCode.startsWith('INV-') || looksLikeInvoiceLink) {
            setCodeError('')
            navigate(`/pay/invoice/${encodeURIComponent(normalizedCode)}`)
            return
        }

        setCodeError('This code was not found. Please use a real merchant payment reference or invoice link.')
    }

    const toggleButtonStyle = (selected, darkText) => ({
        color: selected
            ? `${darkText ? '#000000' : '#FFFFFF'} !important`
            : '#FFFFFF',
        fontWeight: 800,
    })

    return (
        <Box sx={{ minHeight: '100vh', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="lg">
                <Stack direction="row" justifyContent="space-between" mb={4}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(-1)}
                        sx={{ fontWeight: 600 }}
                    >
                        Back
                    </Button>

                    <Chip
                        icon={residency === 'DIASPORA' ? <Public /> : <Place />}
                        label={residency === 'DIASPORA' ? 'Diaspora flow' : 'Local flow'}
                        variant="outlined"
                        color="primary"
                    />
                </Stack>

                <Typography component="h1" variant="h3" fontWeight={900} mb={1}>
                    {accountType === 'USER' ? 'Scan to Pay' : 'Search Businesses'}
                </Typography>

                <Typography color="text.secondary" mb={4}>
                    {accountType === 'USER'
                        ? 'Paste or enter a payment code or invoice code first, just like the mobile app. MojoPayr will open the matching payment flow.'
                        : 'Diaspora users choose the country they are paying into. Local users stay in their resident market and search directly for the business.'}
                </Typography>

                <Paper
                    sx={{
                        p: 3,
                        mb: 4,
                        borderRadius: 4,
                        background:
                            'linear-gradient(180deg, rgba(19,16,44,0.96), rgba(12,11,31,0.96))',
                        border: '1px solid rgba(255,255,255,0.08)',
                    }}
                >
                    <Stack spacing={3}>
                        <Stack spacing={1.5}>
                            <Typography fontWeight={800}>Choose profile</Typography>
                            <ToggleButtonGroup
                                color="primary"
                                exclusive
                                value={accountType}
                                onChange={(_, next) => next && setAccountType(next)}
                                fullWidth
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        color: '#FFFFFF',
                                        borderColor: 'rgba(255,255,255,0.08)',
                                        textTransform: 'none',
                                        py: 1.4,
                                    },
                                    '& .Mui-selected': {
                                        background:
                                            'linear-gradient(90deg,#FF8A00,#FFB347) !important',
                                    },
                                }}
                            >
                                <ToggleButton
                                    value="USER"
                                    sx={toggleButtonStyle(accountType === 'USER', true)}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Payments fontSize="small" />
                                        <span>User</span>
                                    </Stack>
                                </ToggleButton>
                                <ToggleButton
                                    value="BUSINESS"
                                    sx={toggleButtonStyle(accountType === 'BUSINESS', true)}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <BusinessCenter fontSize="small" />
                                        <span>Business</span>
                                    </Stack>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>

                        <Stack spacing={1.5}>
                            <Typography fontWeight={800}>Where are you using MojoPayr from?</Typography>
                            <ToggleButtonGroup
                                color="primary"
                                exclusive
                                value={residency}
                                onChange={(_, next) => next && setResidency(next)}
                                fullWidth
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        color: '#FFFFFF',
                                        borderColor: 'rgba(255,255,255,0.08)',
                                        textTransform: 'none',
                                        py: 1.4,
                                    },
                                    '& .Mui-selected': {
                                        background:
                                            'linear-gradient(90deg,#5B2EFF,#7D52FF) !important',
                                    },
                                }}
                            >
                                <ToggleButton
                                    value="DIASPORA"
                                    sx={toggleButtonStyle(residency === 'DIASPORA', false)}
                                >
                                    Diaspora
                                </ToggleButton>
                                <ToggleButton
                                    value="LOCAL"
                                    sx={toggleButtonStyle(residency === 'LOCAL', false)}
                                >
                                    Local
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                                select
                                label={residency === 'DIASPORA' ? 'Resident Country' : 'Resident Market'}
                                value={residentCountry}
                                onChange={(e) => setResidentCountry(e.target.value)}
                                sx={{ minWidth: { md: 240 } }}
                                fullWidth
                            >
                                {residentCountryOptions.map((item) => (
                                    <MenuItem key={item.code} value={item.code}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {accountType === 'BUSINESS' && residency === 'DIASPORA' ? (
                                <TextField
                                    select
                                    label="Pay Into Country"
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}
                                    sx={{ minWidth: { md: 240 } }}
                                    fullWidth
                                >
                                    <MenuItem value="" disabled>
                                        Select receiving country
                                    </MenuItem>
                                    {operatingCountries.map((item) => (
                                        <MenuItem key={item.code} value={item.code}>
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ) : accountType === 'BUSINESS' ? (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        flex: 1,
                                        p: 2,
                                        borderRadius: 3,
                                        backgroundColor: 'rgba(255,255,255,0.02)',
                                        borderColor: 'rgba(255,255,255,0.08)',
                                    }}
                                >
                                    <Typography fontWeight={700} mb={0.5}>
                                        {accountType === 'USER' ? 'Code-first payment' : 'Search country'}
                                    </Typography>
                                    <Typography color="text.secondary" variant="body2">
                                        {accountType === 'USER'
                                            ? 'Users enter a payment code or invoice code first, then MojoPayr opens the matching flow.'
                                            : 'MojoPayr detects your local market, so no extra country selection is needed.'}
                                    </Typography>
                                </Paper>
                            ) : null}
                        </Stack>

                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                        {accountType === 'USER' ? (
                            <Stack spacing={2}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        backgroundColor: 'rgba(91,46,255,0.12)',
                                        border: '1px solid rgba(183,166,255,0.16)',
                                    }}
                                >
                                    <Typography fontWeight={800} mb={0.5}>
                                        Paste or enter a payment code
                                    </Typography>
                                    <Typography color="text.secondary" variant="body2">
                                        Use a merchant payment reference or invoice code and MojoPayr will open the matching payment review.
                                    </Typography>
                                </Box>

                                <TextField
                                    fullWidth
                                    label="Enter payment code or invoice code"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    error={Boolean(codeError)}
                                    helperText={codeError || 'Enter the real payment code or invoice code from the merchant.'}
                                />

                                <Button
                                    variant="contained"
                                    onClick={handleProcessCode}
                                    disabled={codeLoading}
                                    sx={{
                                        minWidth: 170,
                                        fontWeight: 800,
                                        background: 'linear-gradient(90deg,#FF8A00,#FFB347)',
                                        color: '#0B0620',
                                    }}
                                >
                                    {codeLoading ? 'Processing...' : 'Process Code'}
                                </Button>
                            </Stack>
                        ) : (
                            <>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                    <TextField
                                        fullWidth
                                        label="Search business, school, utility, or service"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Example: EDSA, school fees, pharmacy"
                                    />

                                    <Button
                                        variant="contained"
                                        startIcon={<Search />}
                                        disabled={searchDisabled}
                                        sx={{
                                            minWidth: 170,
                                            fontWeight: 800,
                                            background: 'linear-gradient(90deg,#FF8A00,#FFB347)',
                                            color: '#0B0620',
                                        }}
                                    >
                                        Search
                                    </Button>
                                </Stack>

                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        backgroundColor: 'rgba(255,138,0,0.08)',
                                        border: '1px solid rgba(255,138,0,0.16)',
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={1.5}
                                        justifyContent="space-between"
                                    >
                                        <Box>
                                            <Typography fontWeight={800}>
                                                {residency === 'DIASPORA'
                                                    ? 'Cross-border payment search'
                                                    : 'Local payment search'}
                                            </Typography>
                                            <Typography color="text.secondary" variant="body2">
                                                {residency === 'DIASPORA'
                                                    ? `You will search businesses in ${targetCountryDetails?.label ?? 'the selected country'} and pay from your resident currency with remittance-style FX and fees.`
                                                    : `You will search businesses in ${findCountry(country)?.label ?? 'your resident market'} and pay with local pricing designed to stay lighter than typical mobile money charges.`}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={
                                                targetCountryDetails
                                                    ? `${targetCountryDetails.label} · ${targetCountryDetails.currency}`
                                                    : 'Select target country'
                                            }
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Stack>
                                </Box>
                            </>
                        )}
                    </Stack>
                </Paper>

                {accountType === 'BUSINESS' && (
                    <Stack spacing={2}>
                        {providers.map((provider) => {
                            const verified = provider.status === 'verified'
                            const paymentProfile = getBusinessPaymentProfile(provider)
                            const flowSummary =
                                paymentProfile.flow === 'bill'
                                    ? paymentProfile.accountLabel || 'Account details first'
                                    : paymentProfile.amountHint || 'Amount-based merchant checkout'

                            return (
                                <Card
                                    key={provider.id}
                                    sx={{
                                        borderRadius: 4,
                                        backgroundColor: 'background.paper',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                    }}
                                >
                                    <CardContent>
                                        <Stack
                                            direction={{ xs: 'column', md: 'row' }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: 'flex-start', md: 'center' }}
                                            spacing={2}
                                        >
                                            <Box>
                                                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                                    <Typography fontWeight={900}>
                                                        {provider.business_name}
                                                    </Typography>
                                                    {verified && (
                                                        <Chip
                                                            icon={<Verified />}
                                                            label="Verified"
                                                            color="primary"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Stack>

                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                    {provider.city} · {findCountry(provider.country)?.label} · {provider.category}
                                                </Typography>

                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    <Chip label={provider.category} size="small" />
                                                    <Chip label={`Pays in ${provider.payoutCurrency}`} size="small" />
                                                    <Chip label={flowSummary} size="small" />
                                                    <Chip label="Business profile" size="small" />
                                                </Stack>
                                            </Box>

                                            <Button
                                                variant="contained"
                                                startIcon={<Payments />}
                                                onClick={() => goAmount(provider)}
                                                sx={{
                                                    fontWeight: 800,
                                                    background: 'linear-gradient(90deg,#FF8A00,#FFB347)',
                                                    color: '#0B0620',
                                                    minWidth: 150,
                                                }}
                                            >
                                                Continue
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )
                        })}

                        {providers.length === 0 && (
                            <Paper
                                sx={{
                                    p: 3,
                                    borderRadius: 4,
                                    backgroundColor: 'background.paper',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                }}
                            >
                                <Stack spacing={1}>
                                    <Typography fontWeight={800}>
                                        {searchDisabled
                                            ? 'Choose a receiving country first'
                                            : 'No business matches yet'}
                                    </Typography>
                                    <Typography color="text.secondary">
                                        {searchDisabled
                                            ? 'Diaspora business users need to choose the country they are paying into before searching.'
                                            : 'Try a business name, city, school, utility, pharmacy, or telecom keyword.'}
                                    </Typography>
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                )}
            </Container>
        </Box>
    )
}

export default PaymentServices
